import {ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import {twMerge} from 'tailwind-merge'
import {useTranslation} from 'react-i18next'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import Dialog from '../../../Dialog'
import useDialog from '../../../../hooks/useDialog'
import useURI from '../../../../hooks/useURI'
import {
    confirmCreditPackOrder,
    createCreditPackOrder,
    CreditPackOrderError,
    CreditPackOrderResult,
    getCreditPackOrder,
    paymentsQueries,
} from '../../../../services/payments'
import {workspaceQueries} from '../../../../services/workspace'
import {requestPay} from '../../../../utils/portone'
import {
    excludeVat,
    expiryOfPurchaseToday,
    formatNumber,
    formatSentenceDate,
    noticeDaysOf,
    productNoticeValuesOf,
} from './format'

// 크레딧 팩 구매 모달 — 화면 전달본 P2(팩 선택) · P3(결제 결과), 문안은 정책 16.9 확정본 그대로.
// BE 계약: aipark-front #279 코멘트 (aivatar PR #538 · #543)

/** 모바일 결제(m_redirect_url) 복귀를 알아보는 질의값. 포트원이 imp_uid · merchant_uid · error_msg 를 덧붙인다 */
export const CREDIT_PACK_RETURN_PARAM = 'creditPackReturn'

/** 확인 중이 이 시간을 넘으면 "확인 지연" 문안으로 바꾼다(정책 16.9) */
const CONFIRM_DELAY_MS = 10_000
/** PENDING 일 때 결과 조회 간격 */
const POLL_INTERVAL_MS = 2_000
/** 결과 조회를 그만두는 시점. 그 뒤에도 지급은 웹훅으로 끝나므로 고객은 보유 크레딧 · 청구 내역에서 확인한다 */
const POLL_MAX_ATTEMPTS = 60

type Phase =
    | { step: 'select' }
    | { step: 'paying' }
    /** poll: 확정 응답이 PENDING 이거나 확정 요청이 응답 없이 끝나 결과 조회로 다시 묻는 중 */
    | { step: 'confirming'; merchantUid: string; poll: boolean }
    | { step: 'paid'; result: CreditPackOrderResult }
    | { step: 'failed'; message: string | null }
    | { step: 'cancelled' }

export type CreditPackPaymentReturn = {
    impUid: string | null
    merchantUid: string
    errorMsg: string | null
}

type Props = {
    workspaceId: number
    /** 모바일 결제 화면에서 페이지 이동으로 돌아왔을 때 — 같은 3단계 화면을 이어서 연다 */
    paymentReturn?: CreditPackPaymentReturn
}

function initialPhaseOf(paymentReturn?: CreditPackPaymentReturn): Phase {
    if (!paymentReturn) return {step: 'select'}
    if (!paymentReturn.impUid) return {step: 'failed', message: paymentReturn.errorMsg}
    return {step: 'confirming', merchantUid: paymentReturn.merchantUid, poll: false}
}

export default function CreditPackModal({workspaceId, paymentReturn}: Props) {
    const dialog = useDialog()
    const {i18n} = useTranslation()
    const {uri} = useURI()
    const queryClient = useQueryClient()
    const {data: catalog, isLoading, isError} = useQuery({
        ...paymentsQueries.creditPacks(workspaceId),
        enabled: Boolean(workspaceId),
    })
    const [phase, setPhase] = useState<Phase>(() => initialPhaseOf(paymentReturn))
    const [delayed, setDelayed] = useState(false)
    const [selectedId, setSelectedId] = useState<number | null>(null)

    const aliveRef = useRef(true)
    useEffect(() => {
        aliveRef.current = true
        return () => {
            aliveRef.current = false
        }
    }, [])

    const packs = catalog?.packs ?? []
    const terms = catalog?.terms ?? null
    const noticeValues = productNoticeValuesOf(terms)
    const selectedPack = packs.find((pack) => pack.id === selectedId) ?? null

    /** 서버 결과로만 화면을 정한다. PENDING 이면 확인 중에 머물며 결과 조회로 다시 묻는다 */
    const applyResult = useCallback(
        (result: CreditPackOrderResult) => {
            if (!aliveRef.current) return
            switch (result.result) {
                case 'PAID':
                    // 잔액 · 청구 내역은 workspace detail 하위 쿼리라 부모만 무효화하면 함께 다시 읽는다
                    queryClient.invalidateQueries({queryKey: workspaceQueries.detail(workspaceId).queryKey})
                    setPhase({step: 'paid', result})
                    break
                case 'FAILED':
                    setPhase({step: 'failed', message: result.failMessage})
                    break
                case 'CANCELLED':
                    setPhase({step: 'cancelled'})
                    break
                default:
                    setPhase({step: 'confirming', merchantUid: result.merchantUid, poll: true})
            }
        },
        [queryClient, workspaceId],
    )

    const confirm = useCallback(
        async (merchantUid: string, impUid: string) => {
            setDelayed(false)
            setPhase({step: 'confirming', merchantUid, poll: false})
            try {
                applyResult(await confirmCreditPackOrder({workspaceId, merchantUid, impUid}))
            } catch (e) {
                // 응답을 못 받으면(타임아웃 등) 확인 중으로 두고 결과 조회로 묻는다 — 웹훅이 확정한다
                // eslint-disable-next-line no-console
                console.error('[credit-pack] confirm error', e)
                if (aliveRef.current) setPhase({step: 'confirming', merchantUid, poll: true})
            }
        },
        [applyResult, workspaceId],
    )

    // 모바일 복귀: 복귀 페이지의 질의값으로 같은 확정을 부른다(다시 불러도 지급은 한 번)
    const returnHandledRef = useRef(false)
    useEffect(() => {
        if (returnHandledRef.current || !paymentReturn?.impUid) return
        returnHandledRef.current = true
        confirm(paymentReturn.merchantUid, paymentReturn.impUid)
    }, [confirm, paymentReturn])

    const confirmingMerchantUid = phase.step === 'confirming' ? phase.merchantUid : null
    const polling = phase.step === 'confirming' && phase.poll

    // 확인 중이 10초를 넘으면 확인 지연으로 바꾼다. 그 뒤에 결과가 오면 완료 · 실패 · 취소로 넘어간다
    useEffect(() => {
        if (!confirmingMerchantUid) return undefined
        const timer = window.setTimeout(() => setDelayed(true), CONFIRM_DELAY_MS)
        return () => window.clearTimeout(timer)
    }, [confirmingMerchantUid])

    useEffect(() => {
        if (!polling || !confirmingMerchantUid) return undefined
        let attempts = 0
        let timer: number | undefined
        let stopped = false

        const poll = async () => {
            attempts += 1
            try {
                const result = await getCreditPackOrder(workspaceId, confirmingMerchantUid)
                if (stopped) return
                if (result.result !== 'PENDING') {
                    applyResult(result)
                    return
                }
            } catch (e) {
                // 조회 실패는 다음 회차에 다시 묻는다
                // eslint-disable-next-line no-console
                console.error('[credit-pack] order lookup error', e)
            }
            if (!stopped && attempts < POLL_MAX_ATTEMPTS) {
                timer = window.setTimeout(poll, POLL_INTERVAL_MS)
            }
        }
        timer = window.setTimeout(poll, POLL_INTERVAL_MS)

        return () => {
            stopped = true
            window.clearTimeout(timer)
        }
    }, [polling, confirmingMerchantUid, workspaceId, applyResult])

    const onClickPay = async () => {
        if (!selectedPack || !noticeValues) return

        setPhase({step: 'paying'})

        // ① 주문 생성 — 금액은 서버가 팩에서 정한다
        let order
        try {
            order = await createCreditPackOrder({workspaceId, packId: selectedPack.id})
        } catch (e) {
            // @ts-ignore - axios error shape
            const body: CreditPackOrderError | undefined = e?.response?.data
            // eslint-disable-next-line no-console
            console.error('[credit-pack] create order error', e)
            // 구매 자격 · 판매 상태가 바뀐 경우라 판매 목록을 다시 읽는다(자격이 없으면 진입점이 사라진다)
            queryClient.invalidateQueries({queryKey: paymentsQueries.creditPacks(workspaceId).queryKey})
            // body.message 는 서버 로그용 문장(workspaceId · 판정값 포함)이라 고객에게 보이지 않는다.
            // 결제창을 열기 전이라 결제대행사 메시지도 없으므로 실패 안내의 고정 문안만 보인다
            // eslint-disable-next-line no-console
            console.error('[credit-pack] order rejected', body?.code, body?.message)
            if (aliveRef.current) setPhase({step: 'failed', message: null})
            return
        }

        // ② 결제창 — 서버가 준 값을 그대로 넣는다
        const resp = await requestPay({
            merchant_uid: order.merchantUid,
            amount: Number(order.amount),
            name: order.orderName,
            language: i18n.language,
            m_redirect_url: `${window.location.origin}/workspace/${workspaceId}/subscription?${CREDIT_PACK_RETURN_PARAM}=1`,
        })
        if (!aliveRef.current) return

        // ③ 결제 확정 — 콜백이 성공이든 실패든(창 닫기 · 취소 포함) 항상 부른다. 취소는 웹훅이 오지 않아
        //    확정을 안 부르면 실패 사유를 받을 수 없다. imp_uid 가 아예 없을 때만 확정 없이 실패로 보인다
        if (!resp.imp_uid) {
            // eslint-disable-next-line no-console
            console.error('[credit-pack] request_pay without imp_uid', resp)
            setPhase({step: 'failed', message: resp.error_msg ?? null})
            return
        }
        await confirm(order.merchantUid, resp.imp_uid)
    }

    const onClickRetry = () => {
        setDelayed(false)
        setPhase({step: 'select'})
    }

    // 결제창이 떠 있거나 확인 중일 때는 닫지 않는다. 확인 지연부터는 닫아도 된다(지급은 웹훅으로도 끝난다)
    const closable = phase.step !== 'paying' && !(phase.step === 'confirming' && !delayed)
    const onClose = () => {
        if (closable) dialog.close()
    }

    const stepNumber = phase.step === 'select' ? 1 : phase.step === 'paying' ? 2 : 3

    return (
        <Dialog onClose={onClose} classes={{paper: '!m-auto w-[520px] !rounded-[10px] !p-[30px]'}}>
            <div className="flex w-full flex-col text-left">
                <StepIndicator current={stepNumber}/>

                {phase.step === 'select' || phase.step === 'paying' ? (
                    <>
                        <div className="mt-[20px] flex w-full flex-col gap-[10px]">
                            {isLoading ? (
                                <p className="text-gray-21 py-[20px] text-center text-[14px]">불러오는 중…</p>
                            ) : isError ? (
                                <p className="text-point-3 py-[20px] text-center text-[14px]">
                                    팩 목록을 불러오지 못했습니다.
                                </p>
                            ) : packs.length === 0 ? (
                                <p className="text-gray-21 py-[20px] text-center text-[14px]">
                                    판매 중인 크레딧 팩이 없습니다.
                                </p>
                            ) : (
                                packs.map((pack) => {
                                    const active = pack.id === selectedId
                                    return (
                                        <button
                                            key={pack.id}
                                            type="button"
                                            aria-pressed={active}
                                            disabled={phase.step === 'paying'}
                                            onClick={() => setSelectedId(pack.id)}
                                            className={twMerge(
                                                'text-gray-2 flex w-full items-center justify-between rounded-[8px] border px-[18px] py-[16px] text-[15px] font-normal transition-colors',
                                                active ? 'border-gray-2 bg-gray-6 font-bold' : 'border-gray-4 hover:bg-gray-6',
                                            )}
                                        >
                                            <span>{formatNumber(pack.creditAmount)} 크레딧</span>
                                            <span>{formatNumber(excludeVat(pack.amount))}원</span>
                                        </button>
                                    )
                                })
                            )}
                        </div>

                        {terms?.expiryMonths != null ? (
                            <p className="text-gray-2 mt-[16px] text-[14px] font-bold">
                                사용 기한: {formatSentenceDate(expiryOfPurchaseToday(terms.expiryMonths))}까지
                            </p>
                        ) : null}

                        {noticeValues ? (
                            <div className="bg-gray-6 text-gray-21 mt-[12px] flex flex-col gap-[6px] rounded-[8px] px-[16px] py-[14px] text-[13px] leading-[1.6]">
                                <p>크레딧 팩은 정기구독을 이용하시는 동안 생성량을 더 쓰기 위한 부가 상품입니다.</p>
                                {/* 카드사 심사 보완(토스페이먼츠, 2026-10-15) — 정기결제 · 자동 갱신이 아님을 화면에 밝힌다 */}
                                <p>
                                    크레딧 팩은 정기결제가 아닌 1회 결제 상품으로, 자동으로 갱신되거나 다시 결제되지
                                    않습니다.
                                </p>
                                <p>
                                    구매일로부터 {noticeValues.expiryPeriod}이 지나면 남은 크레딧은 소멸하며, 소멸 후에는
                                    환급되지 않습니다. 소멸일 {noticeValues.firstNoticeDays}일 전과{' '}
                                    {noticeValues.secondNoticeDays}일 전에 워크스페이스 소유자 이메일로 미리
                                    알려드립니다.
                                </p>
                                <p>
                                    구독하지 않는 기간에는 사용할 수 없고, 그 기간에도 소멸일은 그대로 진행됩니다. 다시
                                    구독하시면 남은 크레딧을 이어서 사용하실 수 있습니다.
                                </p>
                                <p>
                                    소멸일까지 언제든 환불을 신청하실 수 있습니다. 구매 후{' '}
                                    {noticeValues.withdrawalPeriodDays}일 이내에는 위약금이 없고, 이후에는 사용률에 따라
                                    미사용분에서 위약금(사용률 {noticeValues.lowUsageThreshold}% 이하{' '}
                                    {noticeValues.lowUsageRate}%, {noticeValues.lowUsageThreshold}% 초과{' '}
                                    {noticeValues.highUsageRate}%)을 공제합니다.
                                </p>
                                {/* 카드사 심사 보완 — 새 탭으로 열어 구매 모달은 그대로 둔다 */}
                                <p>
                                    <a
                                        href={uri['aivatar-term']}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline"
                                    >
                                        환불 정책 자세히 보기
                                    </a>
                                </p>
                                <p>표시 가격은 부가세 별도입니다.</p>
                            </div>
                        ) : null}

                        <p className="text-gray-21 mt-[12px] text-[13px] leading-[1.6]">
                            결제하기를 누르면 열리는 결제 화면에서 카드 정보를 입력합니다. 구독 정기결제에 등록된
                            카드는 사용하지 않습니다.
                        </p>

                        <ButtonRow>
                            <SecondaryButton onClick={onClose} disabled={!closable}>
                                닫기
                            </SecondaryButton>
                            {/* 상품 고지가 없으면 결제로 넘어가지 않는다 — 소멸 · 환불 조항은 구매 전에 보였어야 성립한다 */}
                            <PrimaryButton
                                onClick={onClickPay}
                                disabled={phase.step === 'paying' || !selectedPack || !noticeValues}
                            >
                                결제하기
                            </PrimaryButton>
                        </ButtonRow>
                    </>
                ) : null}

                {phase.step === 'confirming' ? (
                    delayed ? (
                        <>
                            <ResultTitle>결제 확인이 늦어지고 있습니다</ResultTitle>
                            <ResultBody>
                                결제가 확인되면 크레딧이 자동으로 지급됩니다. 이 창을 닫으셔도 됩니다. 잠시 뒤 보유
                                크레딧과 청구 내역에서 확인해 주세요.
                            </ResultBody>
                            <ButtonRow>
                                <SecondaryButton onClick={onClose}>닫기</SecondaryButton>
                            </ButtonRow>
                        </>
                    ) : (
                        <>
                            <ResultTitle>결제를 확인하고 있습니다</ResultTitle>
                            <ResultBody>잠시만 기다려 주세요.</ResultBody>
                        </>
                    )
                ) : null}

                {phase.step === 'paid' ? (
                    <PaidResult result={phase.result} noticeDays={noticeDaysOf(terms)} onConfirm={onClose}/>
                ) : null}

                {phase.step === 'failed' ? (
                    <>
                        <ResultTitle>결제가 완료되지 않았습니다</ResultTitle>
                        {phase.message ? (
                            <p className="text-gray-2 mt-[12px] break-keep text-[14px]">{phase.message}</p>
                        ) : null}
                        <ResultBody>
                            크레딧은 지급되지 않았고 결제 금액도 청구되지 않았습니다. 다시 시도해 주세요.
                        </ResultBody>
                        <ButtonRow>
                            <SecondaryButton onClick={onClose}>닫기</SecondaryButton>
                            <PrimaryButton onClick={onClickRetry}>다시 시도</PrimaryButton>
                        </ButtonRow>
                    </>
                ) : null}

                {phase.step === 'cancelled' ? (
                    <>
                        <ResultTitle>결제를 취소했습니다</ResultTitle>
                        <ResultBody>
                            결제 금액을 확인하는 중 문제가 있어 결제를 취소했습니다. 크레딧은 지급되지 않았고, 결제된
                            금액은 카드사를 통해 취소됩니다.
                        </ResultBody>
                        <ButtonRow>
                            <SecondaryButton onClick={onClose}>닫기</SecondaryButton>
                        </ButtonRow>
                    </>
                ) : null}
            </div>
        </Dialog>
    )
}

function PaidResult({
    result,
    noticeDays,
    onConfirm,
}: {
    result: CreditPackOrderResult
    noticeDays: [number, number] | null
    onConfirm: () => void
}) {
    return (
        <>
            <ResultTitle>크레딧 팩 구매가 완료되었습니다</ResultTitle>
            <dl className="border-gray-4 mt-[16px] flex flex-col gap-[10px] rounded-[8px] border px-[18px] py-[16px] text-[14px]">
                <PaidRow label="지급된 크레딧" value={formatNumber(result.grantedCredit)}/>
                <PaidRow label="결제 금액" value={`${formatNumber(result.amount)}원 (부가세 포함)`}/>
                {result.expiresOn ? (
                    <PaidRow label="사용 기한" value={`${formatSentenceDate(result.expiresOn)}까지`}/>
                ) : null}
                {result.availableCredit != null ? (
                    <PaidRow label="보유 크레딧" value={formatNumber(result.availableCredit)}/>
                ) : null}
            </dl>
            <ResultBody>
                보유 크레딧 중 구독 크레딧을 먼저 사용하고, 크레딧 팩은 가장 나중에 사용됩니다.
                {/* 안내 시점이 서버에 설정돼 있지 않으면 그 문장은 띄우지 않는다(#279 terms 규칙) */}
                {noticeDays
                    ? ` 소멸일 ${noticeDays[0]}일 전과 ${noticeDays[1]}일 전에 워크스페이스 소유자 이메일로 미리 알려드립니다.`
                    : null}
            </ResultBody>
            <ButtonRow>
                <PrimaryButton onClick={onConfirm}>확인</PrimaryButton>
            </ButtonRow>
        </>
    )
}

function PaidRow({label, value}: { label: string; value: string }) {
    return (
        <div className="flex items-center justify-between gap-[12px]">
            <dt className="text-gray-21">{label}</dt>
            <dd className="text-gray-2 font-bold">{value}</dd>
        </div>
    )
}

const STEPS = ['팩 선택', '결제', '완료']

function StepIndicator({current}: { current: number }) {
    return (
        <ol className="text-gray-21 flex items-center gap-[8px] text-[13px]">
            {STEPS.map((label, index) => {
                const number = index + 1
                return (
                    <li key={label} className="flex items-center gap-[8px]">
                        {index > 0 ? <span aria-hidden>·</span> : null}
                        <span
                            aria-current={number === current ? 'step' : undefined}
                            className={twMerge(number === current && 'text-gray-2 font-bold')}
                        >
                            {number}. {label}
                        </span>
                    </li>
                )
            })}
        </ol>
    )
}

function ResultTitle({children}: { children: ReactNode }) {
    return <h3 className="text-gray-2 mt-[24px] text-[20px] font-bold">{children}</h3>
}

function ResultBody({children}: { children: ReactNode }) {
    return <p className="text-gray-21 mt-[12px] break-keep text-[14px] leading-[1.6]">{children}</p>
}

function ButtonRow({children}: { children: ReactNode }) {
    return <div className="mt-[24px] flex w-full justify-end gap-[10px]">{children}</div>
}

type ButtonProps = { children: ReactNode; onClick: () => void; disabled?: boolean }

function SecondaryButton({children, onClick, disabled}: ButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="border-gray-4 text-gray-2 hover:bg-gray-6 h-[40px] min-w-[90px] rounded-[8px] border px-[20px] text-[14px] font-normal transition-colors disabled:opacity-50"
        >
            {children}
        </button>
    )
}

function PrimaryButton({children, onClick, disabled}: ButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="bg-gray-2 h-[40px] min-w-[90px] rounded-[8px] px-[20px] text-[14px] font-normal text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
            {children}
        </button>
    )
}
